import {normalizeDocumentPayload} from '@app/utils/document-validation';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '@env/environment';
import {map, Observable} from 'rxjs';
import {Page} from '../domain/page';
import {Customer} from '../domain/customer';
import {CustomerPerfil, CustomerPreferenciasContacto, CustomerReposicion, CustomerTimelineEvent} from '../domain/customer-perfil';
import {ClienteSegmento} from '../domain/cliente-segmento';
import {ActividadCliente} from '../domain/actividad-cliente';

@Injectable({
    providedIn: 'root'
})
export class CustomerService {

    baseUrl: string = environment.endpointApi;

    constructor(private http: HttpClient) {
    }

    findDistinctsCustomers(): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/different-customers`);
    }

    findAllPageC(params: string): Observable<Page<Customer>> {
        return this.findAllC(params) as Observable<Page<Customer>>;
    }

    findAllPageWithLoyalty(params: string): Observable<Page<Customer>> {
        const queryParams = params ? `${params}&loyalty=true` : 'loyalty=true';
        return this.findAllPageC(queryParams);
    }

    findAllC(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/customers?${params}`);
    }

    findAllCustomersByMonthBirth(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/customers/operations/events-by-birth?${params}`);
    }

    findById(id: number): Observable<Customer> {
        return this.http.get<Customer>(`${this.baseUrl}/customers/${id}`);
    }

    /** Cartera de clientes: clientes por segmento con última compra, compras y gasto del periodo. */
    findSegmentos(params: string): Observable<Page<ClienteSegmento>> {
        return this.http.get<Page<ClienteSegmento>>(`${this.baseUrl}/customers/segmentos?${params}`);
    }

    /** Ficha 360°: actividad general (compras, notas, anulaciones, créditos y pagos) filtrada y paginada. */
    findActividad(id: number, params: string): Observable<ActividadCliente> {
        return this.http.get<ActividadCliente>(`${this.baseUrl}/customers/${id}/actividad?${params}`);
    }

    findPerfil(id: number): Observable<CustomerPerfil> {
        return this.http.get<CustomerPerfil>(`${this.baseUrl}/customers/${id}/perfil`);
    }

    /** Sin idLoyaltyCustomer trae comprobantes y todas las tarjetas; con él, solo esa tarjeta. */
    findPerfilTimeline(id: number, idLoyaltyCustomer?: number): Observable<CustomerTimelineEvent[]> {
        const query = idLoyaltyCustomer ? `?idLoyaltyCustomer=${idLoyaltyCustomer}` : '';
        return this.http.get<CustomerTimelineEvent[]>(`${this.baseUrl}/customers/${id}/perfil/timeline${query}`);
    }

    actualizarPreferenciasContacto(id: number, preferencias: CustomerPreferenciasContacto): Observable<CustomerPreferenciasContacto> {
        return this.http.put<CustomerPreferenciasContacto>(`${this.baseUrl}/customers/${id}/perfil/preferencias-contacto`, preferencias);
    }

    /** Días de reposición fijados desde la ficha; null = vuelve al automático. */
    fijarReposicion(id: number, idProducto: number, dias: number | null): Observable<CustomerReposicion> {
        return this.http.put<CustomerReposicion>(`${this.baseUrl}/customers/${id}/perfil/reposicion/${idProducto}`, {dias});
    }

    findByTelefono(telefono: string): Observable<Customer | null> {
        return this.http.get<Customer | null>(
            `${this.baseUrl}/customers/operations/by-phone?telefono=${encodeURIComponent(telefono)}`
        );
    }

    findByDocumento(tipoDocumento: string, numeroDocumento: string): Observable<Customer | null> {
        const params = [
            'paginacion=false',
            `filtro=${encodeURIComponent(numeroDocumento)}`,
            'limit=20',
            `tipoDocumento=${encodeURIComponent(tipoDocumento)}`
        ];
        return this.findAllC(params.join('&')).pipe(
            map((customers: Customer[]) => (customers || []).find(customer =>
                `${customer?.tipoDocumento || ''}` === `${tipoDocumento}`
                && `${customer?.numeroDocumento || ''}` === `${numeroDocumento}`
            ) || null)
        );
    }

    save(model: Customer): Observable<Customer> {
        model = normalizeDocumentPayload(model);
        return this.http.post<Customer>(`${this.baseUrl}/customers`, model);
    }

    importFromExcel(formData): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/customers/operations/import`, formData);
    }

    downloadImportTemplate(): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/customers/import/template`, {responseType: 'blob'});
    }

    update(model: Customer): Observable<Customer> {
        model = normalizeDocumentPayload(model);
        return this.http.put<Customer>(`${this.baseUrl}/customers`, model);
    }

    updatePorDefecto(id, model: Customer): Observable<Customer> {
        return this.http.patch<Customer>(`${this.baseUrl}/customers/${id}/default`, model);
    }


    deleteById(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/customers/${id}`);
    }

}
